import { Link } from 'react-router-dom';
import { ChevronRight, Home, AlertCircle, RefreshCw } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';

/**
 * Breadcrumbs component for navigation hierarchy
 * @param {Array} items - Array of {label, href} objects
 * @example <Breadcrumbs items={[{label: 'Clients', href: '/app/clients'}, {label: 'John Doe'}]} />
 */
export function Breadcrumbs({ items = [] }) {
  return (
    <nav className="flex items-center gap-1 text-sm mb-4" aria-label="Breadcrumb" data-testid="breadcrumbs">
      <Link to="/app/dashboard" className="flex items-center gap-1 hover:text-[color:var(--brand-navy)]"
        style={{ color: 'var(--ink-muted)' }}>
        <Home className="w-3.5 h-3.5" />
      </Link>
      {items.map((item, index) => (
        <span key={index} className="flex items-center gap-1">
          <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
          {item.href ? (
            <Link to={item.href} className="hover:text-[color:var(--brand-navy)] hover:underline"
              style={{ color: 'var(--ink-muted)' }}>
              {item.label}
            </Link>
          ) : (
            <span style={{ color: 'var(--navy-ink)' }} className="font-medium">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/**
 * Empty state component for lists and tables
 * @param {string} title - Main message
 * @param {string} description - Secondary text
 * @param {ReactNode} icon - Icon component
 * @param {ReactNode} action - Action button
 */
export function EmptyState({ title, description, icon: Icon, action, className = '' }) {
  return (
    <div className={`text-center py-12 px-6 ${className}`} data-testid="empty-state">
      {Icon && (
        <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(27,42,74,0.05)' }}>
          <Icon className="w-7 h-7" style={{ color: 'var(--ink-muted)' }} />
        </div>
      )}
      <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--navy-ink)' }}>{title}</h3>
      {description && (
        <p className="text-sm mb-4" style={{ color: 'var(--ink-muted)' }}>{description}</p>
      )}
      {action}
    </div>
  );
}

/**
 * Error state component for failed data loads
 */
export function ErrorState({ title = 'Something went wrong', message, onRetry, className = '' }) {
  return (
    <div className={`text-center py-12 px-6 ${className}`} data-testid="error-state">
      <div className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: 'rgba(220,38,38,0.08)' }}>
        <AlertCircle className="w-7 h-7" style={{ color: '#DC2626' }} />
      </div>
      <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--navy-ink)' }}>{title}</h3>
      {message && (
        <p className="text-sm mb-4" style={{ color: 'var(--ink-muted)' }}>{message}</p>
      )}
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Try Again
        </Button>
      )}
    </div>
  );
}

/**
 * Card skeleton for loading states
 */
export function CardSkeleton({ count = 1, className = '' }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-0">
            <Skeleton className="h-36 w-full rounded-t-xl" />
            <div className="p-4 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Table skeleton for loading states
 */
export function TableSkeleton({ rows = 5, columns = 6, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4 items-center py-3 px-4 border-b" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
          {Array.from({ length: columns }).map((_, j) => (
            <Skeleton key={j} className="h-5 flex-1" style={{ maxWidth: j === 0 ? '120px' : j === columns - 1 ? '80px' : '150px' }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Stat card skeleton
 */
export function StatCardSkeleton({ count = 4, className = '' }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="rounded-xl border border-border/80 bg-white">
          <CardContent className="p-4">
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-8 w-3/4" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/**
 * Confirmation dialog hook and component
 */
import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

export function ConfirmDialog({ open, onOpenChange, title, description, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'danger', onConfirm, loading }) {
  const variantStyles = {
    danger: { backgroundColor: '#DC2626', hoverBg: '#B91C1C' },
    warning: { backgroundColor: '#D97706', hoverBg: '#B45309' },
    primary: { backgroundColor: 'var(--brand-navy)', hoverBg: '#0F172A' },
  };
  const style = variantStyles[variant] || variantStyles.danger;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--brand-navy)' }}>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelText}
          </Button>
          <Button onClick={onConfirm} disabled={loading}
            style={{ backgroundColor: style.backgroundColor }}
            data-testid="confirm-dialog-btn">
            {loading ? 'Processing...' : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useConfirmDialog() {
  const [state, setState] = useState({ open: false, title: '', description: '', onConfirm: () => {}, variant: 'danger' });
  
  const confirm = useCallback(({ title, description, onConfirm, variant = 'danger', confirmText, cancelText }) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title,
        description,
        variant,
        confirmText,
        cancelText,
        onConfirm: async () => {
          await onConfirm?.();
          setState(prev => ({ ...prev, open: false }));
          resolve(true);
        },
      });
    });
  }, []);
  
  const close = useCallback(() => {
    setState(prev => ({ ...prev, open: false }));
  }, []);
  
  const DialogComponent = () => (
    <ConfirmDialog
      open={state.open}
      onOpenChange={(open) => setState(prev => ({ ...prev, open }))}
      title={state.title}
      description={state.description}
      variant={state.variant}
      confirmText={state.confirmText}
      cancelText={state.cancelText}
      onConfirm={state.onConfirm}
    />
  );
  
  return { confirm, close, ConfirmDialog: DialogComponent };
}

/**
 * Keyboard shortcuts hook
 */
import { useEffect } from 'react';

export function useKeyboardShortcuts(shortcuts = {}) {
  useEffect(() => {
    const handler = (e) => {
      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      
      // Escape - close modals
      if (key === 'escape' && shortcuts.onEscape) {
        shortcuts.onEscape();
        return;
      }
      
      // Ctrl+K - global search
      if (ctrl && key === 'k' && shortcuts.onSearch) {
        e.preventDefault();
        shortcuts.onSearch();
        return;
      }
      
      // Ctrl+N - new order
      if (ctrl && !shift && key === 'n' && shortcuts.onNewOrder) {
        e.preventDefault();
        shortcuts.onNewOrder();
        return;
      }
      
      // Ctrl+Shift+C - new client
      if (ctrl && shift && key === 'c' && shortcuts.onNewClient) {
        e.preventDefault();
        shortcuts.onNewClient();
        return;
      }
    };
    
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [shortcuts]);
}

/**
 * Print styles component - add to pages that need printing
 */
export function PrintStyles() {
  return (
    <style>{`
      @media print {
        body * { visibility: hidden; }
        .print-area, .print-area * { visibility: visible; }
        .print-area { position: absolute; left: 0; top: 0; width: 100%; }
        .no-print { display: none !important; }
        @page { margin: 1cm; }
      }
    `}</style>
  );
}

export default { 
  Breadcrumbs, 
  EmptyState, 
  ErrorState,
  CardSkeleton,
  TableSkeleton,
  StatCardSkeleton,
  ConfirmDialog, 
  useConfirmDialog, 
  useKeyboardShortcuts, 
  PrintStyles 
};
